import React from 'react';
import { Settings, User, RotateCcw, LogOut, Target } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { useGuestMode } from '~/contexts/GuestModeContext';
import { useLanguage } from '~/contexts/LanguageContext';
import { AvatarDisplay } from '~/features/onboarding/components/AvatarDisplay';
import { LanguageSelectorCompact } from '~/components/ui/language-selector';

export function UserAvatarDropdown() {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const { userProfile, clearAllData } = useGuestMode();
  const navigate = useNavigate();
  const location = useLocation();

  const displayName = `${userProfile.firstName} ${userProfile.lastName}`;

  const handleClearData = async () => {
    try {
      await clearAllData();
    } catch (error) {
      console.error('Clear data error:', error);
    }
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  // Get the actual avatar seed from user profile
  const avatarSeed = userProfile.avatar || 'default';

  // Check if we're in onboarding
  const isOnboarding = location.pathname.includes('/onboarding');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          className="relative h-10 w-10 p-0 hover:bg-accent rounded-lg"
        >
          <AvatarDisplay 
            seed={avatarSeed}
            size={36}
            alt={displayName}
            className="cursor-pointer"
            rounded={false}
          />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent className="w-40" align="end" forceMount>
        {/* User Info Header */}
        <div className="flex items-center px-2 py-1.5 space-x-2">
          <div className="flex flex-col leading-none min-w-0 flex-1">
            <p className="font-medium text-xs truncate">{displayName}</p>
          </div>
        </div>
        
        <DropdownMenuSeparator />
        
        {/* Language Selector */}
        <div className="px-2 py-1">
          <LanguageSelectorCompact />
        </div>
        
        <DropdownMenuSeparator />
        
        {/* Show full menu only when NOT in onboarding */}
        {!isOnboarding && (
          <>
            {/* Profile */}
            <DropdownMenuItem asChild className="py-1.5">
              <div className="flex items-center gap-0" onClick={() => navigate('/profile')}>
                <User className="h-3.5 w-3.5 mr-2" />
                <span className="text-sm">{t('common.profile')}</span>
              </div>
            </DropdownMenuItem>
            
            {/* Goals */}
            <DropdownMenuItem asChild className="py-1.5">
              <div className="flex items-center gap-0" onClick={() => navigate('/goals')}>
                <Target className="h-3.5 w-3.5 mr-2" />
                <span className="text-sm">{t('navigation.goals')}</span>
              </div>
            </DropdownMenuItem>
            
            {/* Settings */}
            <DropdownMenuItem asChild className="py-1.5">
              <div className="flex items-center gap-0" onClick={() => navigate('/settings')}>
                <Settings className="h-3.5 w-3.5 mr-2" />
                <span className="text-sm">{t('common.settings')}</span>
              </div>
            </DropdownMenuItem>
            
            {/* Refresh */}
            <DropdownMenuItem onSelect={handleRefresh} className="py-1.5">
              <div className="flex items-center gap-0">
                <RotateCcw className="h-3.5 w-3.5 mr-2" />
                <span className="text-sm">{t('common.refresh')}</span>
              </div>
            </DropdownMenuItem>
            
            <DropdownMenuSeparator />
          </>
        )}
        
        {/* Clear Data - Always shown */}
        <DropdownMenuItem onSelect={handleClearData} className="py-1.5">
          <div className="flex items-center">
            <LogOut className="h-3.5 w-3.5 mr-2" />
            <span className="text-sm">{t('app.clearAllData')}</span>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
